import { throttle, keys } from 'lodash';
import { NgZone, Injectable } from '@angular/core';
import { CoolLocalStorage } from '@angular-cool/storage';
import { BcConfigService } from 'common/config/provider';
import { Howl } from 'howler';

const STORAGE_KEY = 'audio.enabled';
const THROTTLE_TIME_IN_MSEC = 200;
const AUDIO_PATH = {
  NEW_CHAT: 'audio/new_chat.mp3',
  NEW_EMAIL: 'audio/new_email.mp3',
  ACD_SET: 'audio/set_away.mp3',
  NEW_TEXT_CHAT: 'audio/new_message.mp3',
  NEW_MESSAGE: 'audio/new_generic_message.mp3'
};

@Injectable()
export class AudioService {
  private _initialNotificationSettings;
  private _audioNotificationsStatuses;

  private _acdSetAwayAudio;

  private _newChatArrivedAudio;
  private _newChatMessageArrivedAudio;
  private _newTextChatArrivedAudio;
  private _newTextChatMessageArrivedAudio;
  private _newEmailArrivedAudio;

  private _playAcdSetAwayThrottled;

  private _playNewChatArrivedThrottled;
  private _playNewChatMessageArrivedThrottled;
  private _playNewTextChatArrivedThrottled;
  private _playNewTextChatMessageArrivedThrottled;
  private _playNewEmailArrivedThrottled;

  constructor(private localStorageService: CoolLocalStorage,
              private _ngZone: NgZone,
              private configService: BcConfigService) {
    this._initialNotificationSettings = {
      newChatArrived: true,
      newChatMessageArrived: true,
      newTextChatArrived: true,
      newTextChatMessageArrived: true,
      newEmailArrived: true,
      acdAway: true
    };
    this._audioNotificationsStatuses = this._readAudioNotificationSettings();

    this._acdSetAwayAudio = this.createAudio(this.configService.constructAssetUrl(AUDIO_PATH.ACD_SET));
    this._newChatArrivedAudio = this.createAudio(this.configService.constructAssetUrl(AUDIO_PATH.NEW_CHAT));
    this._newChatMessageArrivedAudio = this.createAudio(this.configService.constructAssetUrl(AUDIO_PATH.NEW_MESSAGE));
    this._newTextChatArrivedAudio = this.createAudio(this.configService.constructAssetUrl(AUDIO_PATH.NEW_TEXT_CHAT));
    this._newTextChatMessageArrivedAudio = this.createAudio(this.configService.constructAssetUrl(AUDIO_PATH.NEW_MESSAGE));
    this._newEmailArrivedAudio = this.createAudio(this.configService.constructAssetUrl(AUDIO_PATH.NEW_EMAIL));

    this._playAcdSetAwayThrottled = throttle(this._playAcdSetAway, THROTTLE_TIME_IN_MSEC);

    this._playNewChatArrivedThrottled = throttle(this._playNewChatArrived, THROTTLE_TIME_IN_MSEC);
    this._playNewChatMessageArrivedThrottled = throttle(this._playNewChatMessageArrived, THROTTLE_TIME_IN_MSEC);
    this._playNewTextChatArrivedThrottled = throttle(this._playNewTextChatArrived, THROTTLE_TIME_IN_MSEC);
    this._playNewTextChatMessageArrivedThrottled = throttle(this._playNewTextChatMessageArrived, THROTTLE_TIME_IN_MSEC);
    this._playNewEmailArrivedThrottled = throttle(this._playNewEmailArrived, THROTTLE_TIME_IN_MSEC);
  }

  createAudio(audioSrc) {
    return new Howl({ src: [audioSrc] });
  }

  playAcdSetAway() {
    return this._playAcdSetAwayThrottled();
  }

  playNewChatArrived() {
    return this._playNewChatArrivedThrottled();
  }

  playNewChatMessageArrived() {
    return this._playNewChatMessageArrivedThrottled();
  }

  playNewTextChatArrived() {
    return this._playNewTextChatArrivedThrottled();
  }

  playNewTextChatMessageArrived() {
    return this._playNewTextChatMessageArrivedThrottled();
  }

  playNewEmailArrived() {
    return this._playNewEmailArrivedThrottled();
  }

  muteAudio() {
    keys(this._audioNotificationsStatuses).forEach(key => {
      this._audioNotificationsStatuses[key] = false;
    });
    this.localStorageService.setItem(STORAGE_KEY, JSON.stringify(this._audioNotificationsStatuses));
  }

  unmuteAudio() {
    keys(this._audioNotificationsStatuses).forEach(key => {
      this._audioNotificationsStatuses[key] = true;
    });
    this.localStorageService.setItem(STORAGE_KEY, JSON.stringify(this._audioNotificationsStatuses));
  }

  getCurrentNotificationSettings() {
    return this._audioNotificationsStatuses;
  }

  _readAudioNotificationSettings() {
    const storageData = this.localStorageService.getItem(STORAGE_KEY);
    if (storageData) {
      return JSON.parse(storageData);
    }

    return this._initialNotificationSettings;
  }

  _playAcdSetAway() {
    this._playNotificationOutsideAngular(this._audioNotificationsStatuses.acdAway, this._acdSetAwayAudio);
  }

  _playNewChatArrived() {
    this._playNotificationOutsideAngular(this._audioNotificationsStatuses.newChatArrived, this._newChatArrivedAudio);
  }

  _playNewChatMessageArrived() {
    this._playNotificationOutsideAngular(this._audioNotificationsStatuses.newChatMessageArrived, this._newChatMessageArrivedAudio);
  }

  _playNewTextChatArrived() {
    this._playNotificationOutsideAngular(this._audioNotificationsStatuses.newTextChatArrived, this._newTextChatArrivedAudio);
  }

  _playNewTextChatMessageArrived() {
    this._playNotificationOutsideAngular(this._audioNotificationsStatuses.newTextChatMessageArrived, this._newTextChatMessageArrivedAudio);
  }

  _playNewEmailArrived() {
    this._playNotificationOutsideAngular(this._audioNotificationsStatuses.newEmailArrived, this._newEmailArrivedAudio);
  }

  _playNotificationOutsideAngular(notificationEnabled: boolean, notificationAudio) {
    if (notificationEnabled) {
      this._ngZone.runOutsideAngular(async () => {
        notificationAudio.stop();
        try {
          await notificationAudio.play();
        } catch (error) {
          console.log('Autoplay policy prevented audio from being played.');
        }
      });
    }
  }
}
